import React from 'react';
import { format } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EnvelopeIcon,
  EyeIcon,
  PencilSquareIcon,
  SquaresPlusIcon,
} from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';

import Dash from '@dashboard/components/Dash';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import FieldEditDialog from '@dashboard/components/FieldEditDialog';
import Link from '@dashboard/components/Link';
import {
  Action,
  ActionGroup,
  OrderActionsColumn,
  OrderCustomerCard,
  OrderFieldsCard,
  OrderQueryParametersCard,
  OrderTableCard,
  SubmissionAlerts,
  resendConfirmationOffReason,
  sortDetailRows,
  actionClass,
} from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { confirm } from '@dashboard/lib/confirm';
import { submissionEmailLogsKey, submissionKey } from '@dashboard/lib/queryKeys';
import getPageTypeLabel, { getPageTypeHeading } from '@dashboard/services/getPageTypeLabel';
import { fetcher } from '@dashboard/services/request';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';

import CancelSubmissionDialog from '../components/CancelSubmissionDialog';

/** The Submission card's Created date, in its own format. */
export const formatSubmissionCreated = createdAt =>
  format(new Date(createdAt), 'd MMM, yyyy, HH:mm');

const SubmissionsDetail = ({ submission }) => {
  const recordHref = usePageRecordHref();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const { mutate: sendConfirmationEmail, isPending: isSendingConfirmationEmail } = useMutation({
    mutationFn: async () =>
      fetcher(`/sellers/${sellerId}/submissions/${submission._id}/send-confirmation-email`, token, {
        method: 'POST',
      }),
    onSuccess: () => toast({ title: 'Confirmation email resent' }),
    onError: error =>
      toast({
        title: 'Failed to send confirmation email',
        description: error?.message,
        type: 'error',
      }),
  });

  const handleSendConfirmationEmail = async () => {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });
    if (confirmed) sendConfirmationEmail();
  };

  const { mutateAsync: updateFields } = useMutation({
    mutationFn: data =>
      fetcher(`/sellers/${sellerId}/submissions/${submission._id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ fields: data }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: submissionKey(submission._id) });
      toast({ title: 'Submission updated' });
    },
    onError: () => toast({ title: 'Failed to update submission fields', type: 'error' }),
  });

  if (!submission) return null;

  // The Submission card's two rows lead the Details table; the two IDs follow as before.
  const details = [
    {
      label: 'Form title',
      copy: submission.page?.title || null,
      value: submission.page?.title ? (
        submission.page._id ? (
          <Link to={`/pages/${submission.page._id}`}>{submission.page.title}</Link>
        ) : (
          submission.page.title
        )
      ) : (
        <Dash />
      ),
    },
    { label: 'Created', value: formatSubmissionCreated(submission.createdAt) },
    {
      label: 'Customer ID',
      value: submission.customer ? `#${submission.customer._id}` : <Dash />,
      copy: submission.customer ? `${submission.customer._id}` : null,
    },
    { label: 'Submission ID', value: `#${submission._id}`, copy: `${submission._id}` },
  ];

  const resendOffReason = resendConfirmationOffReason(submission, 'submission');

  return (
    <>
      <SubmissionAlerts submission={submission} />

      <div className="flex flex-col lg:flex-row max-w-5xl items-start justify-center">
        <div className="order-2 lg:order-1 w-full lg:pr-8">
          <OrderCustomerCard customer={submission.customer} alwaysShow />
          <OrderFieldsCard
            fields={submission.fields}
            page={submission.page}
            updateFields={updateFields}
          />
          <OrderTableCard title="Details" rows={sortDetailRows(details)} />
          <OrderQueryParametersCard queryParameters={submission.queryParameters} />
        </div>

        <div className="order-1 lg:order-2 w-full lg:w-72 shrink-0 px-4 pt-3 lg:pt-[52px]">
          <OrderActionsColumn>
            <ActionGroup title="Submission">
              <CancelSubmissionDialog submission={submission} />
            </ActionGroup>
            <ActionGroup title="Email">
              <Action
                icon={EnvelopeIcon}
                label="Resend confirmation"
                onClick={handleSendConfirmationEmail}
                off={Boolean(resendOffReason)}
                title={resendOffReason}
                loading={isSendingConfirmationEmail}
              />
              {submission.customer?.email ? (
                <EmailCustomerDialog
                  customerEmail={submission.customer.email}
                  livemode={submission.livemode}
                  objectLabel="submissions"
                  endpoint={`/sellers/${sellerId}/submissions/${submission._id}/send-email`}
                  variables={buildEmailVariables({ page: submission.page })}
                  queryKey={submissionEmailLogsKey(submission._id)}
                  emailsPath={`/submissions/${submission._id}/emails`}
                />
              ) : (
                <Action
                  icon={Send}
                  label="Email customer"
                  off
                  title="The customer has no email address"
                />
              )}
            </ActionGroup>
            <ActionGroup title="Customer">
              <Action
                icon={EyeIcon}
                label="View customer"
                to={submission.customer ? recordHref('customers', submission.customer._id) : '#'}
                off={!submission.customer}
                title="No customer on this submission"
              />
            </ActionGroup>
            <ActionGroup title="Fields">
              <FieldEditDialog
                fields={submission.fields}
                page={submission.page}
                updateFields={updateFields}
                trigger={
                  <Link className={actionClass(false, 'cursor-pointer')} onClick={() => {}}>
                    <>
                      <PencilSquareIcon className="w-4 h-4" />
                      Edit fields
                    </>
                  </Link>
                }
              />
            </ActionGroup>
            <ActionGroup title={getPageTypeHeading(submission.page)}>
              <Action
                icon={SquaresPlusIcon}
                label={`View ${getPageTypeLabel(submission.page)}`}
                to={submission.page?._id ? `/pages/${submission.page._id}` : '#'}
                off={!submission.page?._id}
                title="This submission has no form"
              />
            </ActionGroup>
          </OrderActionsColumn>
        </div>
      </div>
    </>
  );
};

export default SubmissionsDetail;
